import { defineStore } from 'pinia'
import type { Toast } from './types'

export const useToastStore = defineStore('toast', () => {
    const current = ref<Toast | null>(null)
    const queue = ref<Toast[]>([])

    type ToastOptions = Omit<Toast, 'message' | 'type'>

    const next = (delay = false) => {
        current.value = null
        const next = queue.value.shift()
        if (!next) return
        // this is to wait for the toast close transition to finish:
        // https://github.com/ubitransports/2cloud/pull/19260
        setTimeout(() => (current.value = next), delay ? 500 : 0)
    }

    const push = (toast: Toast) => {
        const isDuplicateToast = [current.value, ...queue.value].some(
            (queueToast) =>
                toast.message === queueToast?.message &&
                toast.title === queueToast?.title
        )

        const processQueue = () => {
            if (current.value) return
            next()
        }

        if (isDuplicateToast) return
        queue.value.push(toast)
        processQueue()
    }

    const success = (message: string, options?: ToastOptions) => {
        push({
            type: 'success',
            message,
            ...options,
        })
    }

    const info = (message: string, options?: ToastOptions) => {
        push({
            type: 'info',
            message,
            ...options,
        })
    }

    const error = (message: string, options?: ToastOptions) => {
        push({
            type: 'error',
            message,
            ...options,
        })
    }

    const endLoad = (key?: string) => {
        queue.value = queue.value.filter((toast) => toast.key !== key)
        return next()
    }

    const load = (message: string, key: string, options?: ToastOptions) => {
        push({
            type: 'loading',
            message,
            autoclose: false,
            closable: false,
            key,
            ...options,
        })

        return () => endLoad(key)
    }

    return { push, success, info, error, load, next, current }
})
